import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import {
    LatLng,
    LocationID,
    LocationProximity,
    Meters,
    isoLocationID,
} from "../../models";
import { DistanceSelect } from "./DistanceSelect";
import { LocationList } from "./LocationList";
import { UpdateLocationForm } from "./UpdateLocationForm";

import styles from "./index.module.scss";

export interface LocatorMapProps {
    locations: LocationProximity[];
    focusedLocationID: LocationID | null;
    setFocusedLocationID: (id: LocationID | null) => void;
    error: string;
    onLocationSearch: (
        point: LatLng | null,
        bounds?: google.maps.LatLngBounds,
    ) => void;
    onError: (error: string) => void;
    radius: Meters;
    onRadiusChange: (radius: Meters) => void;
    mapSlot: React.ReactNode;
}

export const LocatorMap = (props: LocatorMapProps) => {
    const listRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!listRef.current || !props.focusedLocationID) return;
        const item = listRef.current.querySelector(
            `[data-id="${isoLocationID.get(props.focusedLocationID)}"]`,
        );
        if (item instanceof HTMLElement) {
            item.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [props.focusedLocationID]);

    return (
        <section className={styles.section}>
            <div className={styles.grid}>
                <div className={styles.searchArea}>
                    <UpdateLocationForm
                        onLocationUpdate={props.onLocationSearch}
                        onError={props.onError}
                    />
                </div>
                <div className={styles.distanceSelect}>
                    <DistanceSelect
                        radius={props.radius}
                        onRadiusChange={props.onRadiusChange}
                    />
                </div>
                <a href="#locator-results" className={styles.jumpToResults}>
                    {t`Jump to results`}
                </a>
                <div className={styles.map}>{props.mapSlot}</div>
                <div
                    id="locator-results"
                    className={styles.locationList}
                    ref={listRef}
                >
                    {props.error !== "" && (
                        <p className={styles.locationError}>{props.error}</p>
                    )}
                    {props.locations.length === 0 && (
                        <p className={styles.locationError}>
                            {t`Enter a valid city, state, or ZIP code.`}
                        </p>
                    )}
                    <LocationList
                        locations={props.locations}
                        focusedLocationID={props.focusedLocationID}
                        onSelect={props.setFocusedLocationID}
                    />
                </div>
            </div>
        </section>
    );
};
